import React from 'react';
import type { Breakpoint } from '../../hooks/useBreakpoint';
import { bpConfig, cisData, orgs, orgRawColors } from './data';

interface AssessCardProps {
  mode: 'enterprise' | 'mssp';
  breakpoint: Breakpoint;
}

const statusColor = (s: string) =>
  s === 'pass' ? 'bg-anchor-teal' : s === 'warn' ? 'bg-anchor-amber' : 'bg-anchor-red';

const AssessCard: React.FC<AssessCardProps> = ({ mode, breakpoint }) => {
  const cfg = bpConfig[breakpoint];
  const fontSize = cfg.cisRowFont;
  const labelCol = cfg.cisLabelCol;

  if (mode === 'enterprise') {
    return (
      <div className="glass-card">
        <div className="text-[10px] tracking-[0.12em] uppercase text-anchor-blue mb-1">Step 02 — Assess</div>
        <div className="text-xl font-light text-white/90 mb-4">Assess Against CIS Benchmarks</div>
        <div className="flex flex-col gap-1.5 flex-1 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
          {cisData.map((c, i) => (
            <div
              key={c.num}
              className="grid gap-2.5 items-center py-1.5 px-3.5 bg-white/[0.015] border border-white/[0.025] rounded-[6px]"
              style={{
                gridTemplateColumns: `${labelCol} 1fr auto auto`,
                opacity: 0,
                animation: `cisFadeIn 0.5s ease ${i * 0.07 + 0.1}s forwards`,
              }}
            >
              <span className={`font-medium tracking-[0.02em] ${c.coverage === 'partial' ? 'text-anchor-blue' : 'text-anchor-teal'}`} style={{ fontSize: `${fontSize - 1}px` }}>
                {c.num}
              </span>
              <span className="text-white/70 whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: `${fontSize}px` }}>{c.name}</span>
              <div className={`w-4 h-4 rounded-[3px] ${statusColor(c.status)}`} />
              <span className="text-white/50 min-w-[32px] text-right" style={{ fontSize: `${fontSize}px` }}>{c.pct}%</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // MSSP mode
  return (
    <div className="glass-card">
      <div className="text-[10px] tracking-[0.12em] uppercase text-anchor-blue mb-1">Step 02 — Assess</div>
      <div className="text-xl font-light text-white/90 mb-4">Assess Against CIS Benchmarks</div>
      <div className="flex flex-col gap-1.5 flex-1 overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin' }}>
        {orgs.map((o, oi) => (
          <React.Fragment key={o.name}>
            <div
              className={`flex items-center gap-2.5 py-2 px-3 text-white/80 ${oi > 0 ? 'mt-1.5 border-t border-white/[0.04]' : ''}`}
              style={{ opacity: 0, animation: `cisFadeIn 0.3s ease ${oi * 0.04}s forwards`, fontSize: `${fontSize}px` }}
            >
              <span
                className="w-5 h-5 rounded-[4px] flex items-center justify-center text-[9px] font-medium text-white"
                style={{ background: orgRawColors[oi % orgRawColors.length] }}
              >
                {o.letter}
              </span>
              <span>{o.name}</span>
              <span className="ml-auto text-white/40" style={{ fontSize: `${fontSize - 1}px` }}>{o.pct}%</span>
            </div>
            {cisData.map((c, ci) => {
              const clientPct = Math.max(50, Math.min(100, Math.round(c.pct + (o.pct - 85) * 0.4 + (Math.random() - 0.5) * 10)));
              const status = clientPct >= 90 ? 'pass' : clientPct >= 75 ? 'warn' : 'fail';
              return (
                <div
                  key={`${o.name}-${c.num}`}
                  className="grid gap-1.5 items-center py-1 pr-3 bg-white/[0.015] border border-white/[0.025] rounded-[5px]"
                  style={{
                    gridTemplateColumns: `${labelCol} 1fr auto auto`,
                    paddingLeft: '38px',
                    opacity: 0,
                    animation: `cisFadeIn 0.5s ease ${oi * 0.04 + ci * 0.03 + 0.1}s forwards`,
                  }}
                >
                  <span className={`font-medium tracking-[0.02em] ${c.coverage === 'partial' ? 'text-anchor-blue' : 'text-anchor-teal'}`} style={{ fontSize: `${fontSize - 2}px` }}>
                    {c.num}
                  </span>
                  <span className="text-white/70 whitespace-nowrap overflow-hidden text-ellipsis" style={{ fontSize: `${fontSize - 1}px` }}>{c.name}</span>
                  <div className={`w-3 h-3 rounded-[3px] ${statusColor(status)}`} />
                  <span className="text-white/50 min-w-[28px] text-right" style={{ fontSize: `${fontSize - 1}px` }}>{clientPct}%</span>
                </div>
              );
            })}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default AssessCard;
